import { useEffect, useMemo, useState } from 'react';
import { PiFunnel, PiMagnifyingGlassBold, PiXBold } from 'react-icons/pi';
import { Badge, Button, cn, Input, MultiSelect, Select, Switch } from '@v2/design-system';
import { useI18n } from '@v2/providers/I18nProvider';
import type { FilterOptions, OwnerFilterOption, PloomesDealFilters } from './types';
import DealOwnerCell from './DealOwnerCell';
import { shortOwnerName } from './utils';
import DateRangePicker from '@v2/components/ui/DateRangePicker';
import {
    countActiveFilters,
    FILTER_FIELD_LABELS,
    formatFilterChipValue,
    listActiveFilters,
    SORT_OPTIONS,
} from './filter-config';

type Props = {
    filters: PloomesDealFilters;
    options: FilterOptions;
    optionsLoading?: boolean;
    showPanel: boolean;
    onChange: (patch: Partial<PloomesDealFilters>) => void;
    onTogglePanel: () => void;
    onClear: () => void;
};

type SelectOpt = { label: string; value: string };

function optionsToList(map: Record<string, string> | undefined, emptyLabel = 'Todos'): SelectOpt[] {
    return [
        { label: emptyLabel, value: '' },
        ...Object.entries(map ?? {}).map(([value, label]) => ({ value, label })),
    ];
}

function findOpt(options: SelectOpt[], value: string): SelectOpt {
    return options.find((o) => o.value === value) ?? options[0] ?? { label: 'Todos', value: '' };
}

function OwnerOptionRow({ option }: { option: OwnerFilterOption }) {
    return (
        <div className="flex min-w-0 items-center gap-2 py-0.5">
            <DealOwnerCell name={option.label} avatarUrl={option.avatar_url} showName={false} />
            <span className="truncate text-sm text-gray-800 dark:text-zinc-100" title={option.label}>
                {shortOwnerName(option.label)}
            </span>
        </div>
    );
}

function OwnerMultiSelect({
    value,
    options,
    disabled,
    onChange,
}: {
    value: string[];
    options: OwnerFilterOption[];
    disabled?: boolean;
    onChange: (value: string[]) => void;
}) {
    return (
        <MultiSelect
            label="Responsável"
            size="sm"
            searchable
            stickySearch
            clearable={value.length > 0}
            disabled={disabled}
            options={options}
            value={value}
            placeholder="Todos os vendedores"
            searchPlaceHolder="Digite o nome do vendedor…"
            className="w-full"
            selectClassName="min-h-9"
            dropdownClassName="z-[80]"
            optionCheckBox
            hideSelectedOptions={false}
            onChange={onChange}
            onClear={() => onChange([])}
            getOptionDisplayValue={(option: OwnerFilterOption) => <OwnerOptionRow option={option} />}
            displayValue={(selected, opts, handleClearItem) => {
                if (!selected.length) {
                    return <span className="text-gray-400">Todos os vendedores</span>;
                }

                return (
                    <div className="flex flex-wrap gap-1 py-0.5">
                        {selected.map((name) => {
                            const opt = opts.find((o) => o.value === name) as OwnerFilterOption | undefined;
                            return (
                                <span
                                    key={name}
                                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-0.5 pl-0.5 pr-1.5 text-xs dark:border-zinc-700 dark:bg-zinc-900"
                                >
                                    <DealOwnerCell
                                        name={opt?.label ?? name}
                                        avatarUrl={opt?.avatar_url}
                                        showName={false}
                                    />
                                    <span className="max-w-[7rem] truncate font-medium text-gray-700 dark:text-zinc-200">
                                        {shortOwnerName(opt?.label ?? name)}
                                    </span>
                                    {handleClearItem && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleClearItem(name);
                                            }}
                                            className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                            aria-label={`Remover ${name}`}
                                        >
                                            <PiXBold className="h-3 w-3" />
                                        </button>
                                    )}
                                </span>
                            );
                        })}
                    </div>
                );
            }}
        />
    );
}

function FilterToggle({
    label,
    checked,
    disabled,
    activeClassName,
    onChange,
}: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    activeClassName?: string;
    onChange: (checked: boolean) => void;
}) {
    return (
        <div
            className={cn(
                'flex h-full min-h-9 items-center justify-between gap-3 rounded-lg border px-3 py-2',
                checked
                    ? (activeClassName ?? 'border-orange-200 bg-orange-50/70 dark:border-orange-500/30 dark:bg-orange-500/10')
                    : 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
            )}
        >
            <span className={cn('text-xs font-medium', checked ? 'text-gray-900 dark:text-zinc-100' : 'text-gray-500 dark:text-gray-400')}>
                {label}
            </span>
            <Switch
                size="sm"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                aria-label={label}
            />
        </div>
    );
}

function formatMoneyDisplay(value: string): string {
    if (!value) return '';
    const n = Number(value);
    if (Number.isNaN(n)) return '';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function parseMoneyInput(raw: string): string {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    return (Number(digits) / 100).toFixed(2);
}

function MoneyFilterInput({
    label,
    value,
    disabled,
    onChange,
}: {
    label: string;
    value: string;
    disabled?: boolean;
    onChange: (value: string) => void;
}) {
    return (
        <label className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
            <input
                type="text"
                inputMode="numeric"
                disabled={disabled}
                placeholder="R$ 0,00"
                value={formatMoneyDisplay(value)}
                onChange={(e) => onChange(parseMoneyInput(e.target.value))}
                className="ds-native-input w-full"
            />
        </label>
    );
}

function SearchableFilterSelect({
    label,
    value,
    options,
    disabled,
    onChange,
}: {
    label: string;
    value: string;
    options: SelectOpt[];
    disabled?: boolean;
    onChange: (value: string) => void;
}) {
    const selected = findOpt(options, value);

    return (
        <Select
            label={label}
            size="sm"
            searchable
            stickySearch
            clearable={value !== ''}
            disabled={disabled}
            options={options}
            value={selected}
            placeholder={label}
            searchPlaceHolder="Digite para buscar…"
            className="w-full"
            selectClassName="h-9"
            dropdownClassName="z-[80]"
            onChange={(opt: SelectOpt | null) => onChange(opt?.value ?? '')}
            onClear={() => onChange('')}
        />
    );
}

export default function PloomesDealListFilters({
    filters,
    options,
    optionsLoading,
    showPanel,
    onChange,
    onTogglePanel,
    onClear,
}: Props) {
    const { t } = useI18n();
    const [searchDraft, setSearchDraft] = useState(filters.search);
    const activeCount = countActiveFilters(filters);
    const activeChips = listActiveFilters(filters);

    const ownerOptions = useMemo<OwnerFilterOption[]>(
        () => options.owners ?? [],
        [options.owners],
    );
    const segmentOptions = useMemo(() => optionsToList(options.segment), [options.segment]);
    const classificationOptions = useMemo<SelectOpt[]>(
        () => Object.entries(options.client_classification ?? {}).map(([value, label]) => ({ value, label })),
        [options.client_classification],
    );
    const sortOptions = useMemo(
        () => SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        [],
    );

    useEffect(() => {
        setSearchDraft(filters.search);
    }, [filters.search]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            if (searchDraft !== filters.search) {
                onChange({ search: searchDraft, page: 1 });
            }
        }, 350);
        return () => window.clearTimeout(timer);
    }, [searchDraft, filters.search, onChange]);

    const disabled = optionsLoading;

    return (
        <section className="ds-card">
            <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                <div className="min-w-[200px] flex-1">
                    <Input
                        type="search"
                        placeholder={t('v2.ploomes_deals.search_placeholder')}
                        value={searchDraft}
                        onChange={(e) => setSearchDraft(e.target.value)}
                        onClear={() => { setSearchDraft(''); onChange({ search: '', page: 1 }); }}
                        inputClassName="h-9"
                        clearable
                        prefix={<PiMagnifyingGlassBold className="size-4" />}
                    />
                </div>

                <div className="w-[13rem]">
                    <Select
                        size="sm"
                        searchable
                        options={sortOptions}
                        value={findOpt(sortOptions, filters.sort || '-finish_date')}
                        onChange={(opt: SelectOpt | null) => onChange({ sort: opt?.value || '-finish_date', page: 1 })}
                        selectClassName="h-9"
                        dropdownClassName="z-[80]"
                        aria-label="Ordenar por"
                    />
                </div>

                {activeCount > 0 && (
                    <Button
                        size="sm"
                        variant="text"
                        onClick={onClear}
                        className="h-9 px-2 text-xs text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <PiXBold className="me-1 h-3.5 w-3.5" />
                        Limpar
                    </Button>
                )}

                <Button
                    size="sm"
                    variant={showPanel ? 'solid' : 'outline'}
                    color={showPanel ? 'primary' : undefined}
                    onClick={onTogglePanel}
                    className="h-9 px-2.5 text-xs"
                >
                    <PiFunnel className="me-1 h-3.5 w-3.5" />
                    Filtros
                    {activeCount > 0 && (
                        <Badge
                            variant="flat"
                            color={showPanel ? 'primary' : undefined}
                            className="ms-1.5 min-w-[1.1rem] justify-center px-1 py-0 text-[10px]"
                        >
                            {activeCount}
                        </Badge>
                    )}
                </Button>
            </div>

            {showPanel && (
                <div className="grid grid-cols-1 gap-x-3 gap-y-3 border-t border-gray-100 px-3 py-3 dark:border-gray-700/50 sm:grid-cols-2 lg:grid-cols-3">
                    <div className="sm:col-span-2 lg:col-span-2">
                        <OwnerMultiSelect
                            value={filters.owner_names}
                            options={ownerOptions}
                            disabled={disabled}
                            onChange={(owner_names) => onChange({ owner_names, page: 1 })}
                        />
                    </div>

                    <SearchableFilterSelect
                        label="Segmento"
                        value={filters.segment}
                        options={segmentOptions}
                        disabled={disabled}
                        onChange={(value) => onChange({ segment: value, page: 1 })}
                    />

                    <MultiSelect
                        label="Classificação do cliente"
                        size="sm"
                        searchable
                        stickySearch
                        clearable={filters.client_classifications.length > 0}
                        disabled={disabled || classificationOptions.length === 0}
                        options={classificationOptions}
                        value={filters.client_classifications}
                        placeholder={classificationOptions.length === 0 ? 'Sem dados sincronizados' : 'Todas'}
                        searchPlaceHolder="Buscar classificação…"
                        className="w-full"
                        selectClassName="min-h-9"
                        dropdownClassName="z-[80]"
                        optionCheckBox
                        hideSelectedOptions={false}
                        onChange={(client_classifications: string[]) => onChange({ client_classifications, page: 1 })}
                        onClear={() => onChange({ client_classifications: [], page: 1 })}
                    />

                    <FilterToggle
                        label="Buckler One"
                        checked={filters.buckler_one === '1'}
                        disabled={disabled}
                        activeClassName="border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10"
                        onChange={(checked) => onChange({ buckler_one: checked ? '1' : '', page: 1 })}
                    />

                    <FilterToggle
                        label="Inadimplência"
                        checked={filters.defaulted_only === '1'}
                        disabled={disabled}
                        activeClassName="border-rose-200 bg-rose-50/80 dark:border-rose-500/30 dark:bg-rose-500/10"
                        onChange={(checked) => onChange({ defaulted_only: checked ? '1' : '', page: 1 })}
                    />

                    <FilterToggle
                        label="Parcelas em aberto"
                        checked={filters.open_installments_only === '1'}
                        disabled={disabled}
                        activeClassName="border-orange-200 bg-orange-50/80 dark:border-orange-500/30 dark:bg-orange-500/10"
                        onChange={(checked) => onChange({ open_installments_only: checked ? '1' : '', page: 1 })}
                    />

                    <FilterToggle
                        label="Team Agreement"
                        checked={filters.team_agreement === '1'}
                        disabled={disabled}
                        onChange={(checked) => onChange({ team_agreement: checked ? '1' : '', page: 1 })}
                    />

                    <FilterToggle
                        label="Exceção Ploomes"
                        checked={filters.commission_exception === '1'}
                        disabled={disabled}
                        onChange={(checked) => onChange({ commission_exception: checked ? '1' : '', page: 1 })}
                    />

                    <FilterToggle
                        label="Tem SDR"
                        checked={filters.has_sdr === '1'}
                        disabled={disabled}
                        onChange={(checked) => onChange({ has_sdr: checked ? '1' : '', page: 1 })}
                    />

                    <div className="sm:col-span-2 lg:col-span-3 flex flex-col gap-3 lg:flex-row lg:items-end">
                        <div className="min-w-0 flex-1">
                            <DateRangePicker
                                label="Período de ganho"
                                from={filters.finish_date_from}
                                to={filters.finish_date_to}
                                disabled={disabled}
                                onChange={({ from, to }) => onChange({
                                    finish_date_from: from,
                                    finish_date_to: to,
                                    page: 1,
                                })}
                            />
                        </div>
                        <div className="flex min-w-0 flex-[1.1] gap-3">
                            <MoneyFilterInput
                                label="Valor mín."
                                value={filters.amount_min}
                                disabled={disabled}
                                onChange={(amount_min) => onChange({ amount_min, page: 1 })}
                            />
                            <MoneyFilterInput
                                label="Valor máx."
                                value={filters.amount_max}
                                disabled={disabled}
                                onChange={(amount_max) => onChange({ amount_max, page: 1 })}
                            />
                        </div>
                    </div>
                </div>
            )}

            {activeChips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 border-t border-gray-100 px-3 py-2 dark:border-gray-700/50">
                    {activeChips.map((chip) => (
                        <button
                            key={chip.key}
                            type="button"
                            onClick={() => {
                                if (chip.key === 'owner_names') {
                                    onChange({ owner_names: [], page: 1 });
                                    return;
                                }
                                if (chip.key === 'client_classifications') {
                                    onChange({ client_classifications: [], page: 1 });
                                    return;
                                }
                                if (chip.key === 'finish_date_from') {
                                    onChange({ finish_date_from: '', finish_date_to: '', page: 1 });
                                    return;
                                }
                                onChange({ [chip.key]: '', page: 1 } as Partial<PloomesDealFilters>);
                            }}
                            className={[
                                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition',
                                'border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300 hover:bg-gray-100',
                                'dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700',
                            ].join(' ')}
                        >
                            <span className="text-gray-500 dark:text-gray-500">{FILTER_FIELD_LABELS[chip.key] ?? chip.key}:</span>
                            <span className="font-medium">{formatFilterChipValue(chip.key, chip.value)}</span>
                            <PiXBold className="h-3 w-3 opacity-60" />
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
}
